import { useEffect, useState } from "react";
import { PackageCheck, CheckCircle2, Gift } from "lucide-react";
import { api, apiError, money } from "@/lib/helpers";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

function WishCard({ item, onClaimed }) {
  const [showInput, setShowInput] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const claim = async () => {
    if (!name.trim()) { toast.error("Tell us who to thank!"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "wishlist", ref_id: item.id, name, origin_url: window.location.origin,
      });
      toast.success("Taking you to secure Stripe checkout…");
      setTimeout(() => { window.location.href = data.checkout_url; }, 700);
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid={`wishlist-item-${item.id}`} className={`rounded-3xl border p-6 transition-all ${item.claimed ? "bg-tinted border-stone-200 opacity-80" : "bg-white border-stone-200 hover:shadow-lg hover:-translate-y-1"}`}>
      <div className="flex items-start justify-between gap-3">
        <PackageCheck className={`h-6 w-6 ${item.claimed ? "text-stone-400" : "text-gold-deep"}`} />
        <span className="font-mono text-sm font-bold text-pine">{money(item.cost)}</span>
      </div>
      <h3 className="mt-3 font-serif text-lg text-charcoal leading-snug">{item.title}</h3>
      <p className="mt-1.5 text-sm text-bark">{item.detail}</p>
      {item.claimed ? (
        <p data-testid={`wishlist-claimed-${item.id}`} className="mt-4 flex items-center gap-2 text-sm font-semibold text-pine">
          <CheckCircle2 className="h-4 w-4" /> Claimed{item.claimed_by ? ` by ${item.claimed_by}` : ""} — thank you
        </p>
      ) : showInput ? (
        <div className="mt-4 flex gap-2">
          <input data-testid={`wishlist-name-input-${item.id}`} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && claim()}
            className="min-w-0 flex-1 rounded-full border border-stone-300 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
          <button data-testid={`wishlist-confirm-${item.id}`} onClick={claim} disabled={busy}
            className="rounded-full bg-pine px-4 py-2 text-sm font-bold text-sand hover:bg-pine-mid transition-colors disabled:opacity-60">
            {busy ? "…" : "Confirm"}
          </button>
        </div>
      ) : (
        <button data-testid={`wishlist-claim-${item.id}`} onClick={() => setShowInput(true)}
          className="mt-4 w-full rounded-full bg-gold py-2.5 text-sm font-bold text-charcoal hover:bg-gold-soft transition-all hover:scale-[1.02]">
          I'll give this
        </button>
      )}
    </div>
  );
}

export default function Wishlist() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/wishlist").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  const onClaimed = (id, name) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, claimed: true, claimed_by: name } : i)));

  return (
    <section id="wishlist" className="relative bg-tinted py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="eyebrow">Supplies wishlist</p>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal tracking-tight">
            Give goods. Get a kennel full of gratitude.
          </h2>
          <p className="mt-3 text-bark">Claim an item and we'll send drop-off details — or ship it straight to the mountain.</p>
        </Reveal>

        <Reveal delay={0.05}>
          <div data-testid="inkind-donation-note" className="mb-10 flex flex-col sm:flex-row items-start gap-4 rounded-3xl border border-gold/40 bg-gold/10 p-6 sm:p-7">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gold text-charcoal">
              <Gift className="h-5 w-5" />
            </span>
            <div className="text-sm sm:text-base text-charcoal leading-relaxed">
              <span className="font-bold">Prefer to give items instead of cash? Wonderful.</span> Blankets are always appreciated.
              If it's food, it must be <span className="font-bold">Purina One Smart Blend only</span> — we keep all of our animals on the same food.
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <Reveal key={it.id} delay={(i % 4) * 0.07}><WishCard item={it} onClaimed={onClaimed} /></Reveal>
          ))}
          {!items.length && <p className="font-mono text-sm text-bark">Loading the barn list…</p>}
        </div>
      </div>
    </section>
  );
}
